import { Flex, Tab, Loader } from '@percent/lemonade'
import { GrantDetailsHeader } from './grantDetailsHeader/GrantDetailsHeader'
import { useEffect, useState } from 'react'
import { GrantDetailsHeaderKey } from './grantDetailsHeader/GrantDetailsHeader.types'
import { ProgramOverview } from './programOverview/ProgramOverview'
import { OrganisationDetails } from './organisationDetails/OrganisationDetails'
import { CandidateDocuments } from './candidateDocuments/CandidateDocuments'
import { useGrantDetails } from './hooks/useGrantDetails'
import { useTranslation } from 'react-i18next'
import { CandidateTasks } from './candidateTasks/CandidateTasks'
import { useCandidateTasks } from './hooks/useCandidateTasks'
import type { CandidateData } from '@percent/utility'
import { ErrorState } from '../components/errorState/ErrorState'
import { EmptyState } from '../components/emptyState/EmptyState'
import { useNavigate } from 'react-router-dom'
import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../routes/Routes'
import { DonationDetails } from './donationDetails/DonationDetails'
import { NominationsDetails } from './nominationsDetails/NominationsDetails'

export const MyGrantDetailsPage = () => {
  const [activeTab, setActiveTab] = useState<GrantDetailsHeaderKey>(GrantDetailsHeaderKey.OVERVIEW)

  const { program, candidate, documents, isLoading, error, refetchDocuments } = useGrantDetails()
  const { isTasksViewOpen, openTasksView, closeTasksView, tasks, refetchTasks } = useCandidateTasks({
    candidateId: candidate?.id ?? '',
    programId: program?.id ?? ''
  })

  const { t } = useTranslation()

  useBreadcrumbs(
    !isLoading && program.name !== t('grants.grantDetails.programOverview.unknownName')
      ? [{ label: t('breadcrumb.grants'), path: RoutePath.GRANTS }, { label: program.name }]
      : undefined
  )

  const navigate = useNavigate()

  const hasNominations = (candidate?.nominations?.length ?? 0) > 0
  const hasAward = Boolean(candidate?.award)

  useEffect(() => {
    if (!candidate) {
      return
    }

    const activeTabIsHidden =
      (activeTab === GrantDetailsHeaderKey.NOMINATIONS && !hasNominations) ||
      (activeTab === GrantDetailsHeaderKey.DONATION && !hasAward)

    if (activeTabIsHidden) {
      setActiveTab(GrantDetailsHeaderKey.OVERVIEW)
    }
  }, [activeTab, candidate, hasAward, hasNominations])

  if (isLoading) {
    return <Loader data-testid="loading" />
  }

  if (error) {
    return <ErrorState />
  }

  if (!candidate) {
    return (
      <EmptyState
        illustration="no-grant-programs"
        title={t('grants.noGrantDetails.title')}
        description={t('grants.noGrantDetails.description')}
        cta={{
          label: t('grants.noGrantDetails.backButton'),
          onClick: () => navigate('/my-grants')
        }}
      />
    )
  }

  const handleOpenDocuments = () => {
    setActiveTab(GrantDetailsHeaderKey.DOCUMENTS)
    closeTasksView()
  }

  return (
    <Flex gap={16}>
      <Flex flex={1} direction="column" gap={16} data-testid="my-grant-details-page">
        <GrantDetailsHeader
          programName={program.name}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tasksOpen={isTasksViewOpen}
          setTasksOpen={openTasksView}
          hasTasks={tasks.length > 0}
          hasNominations={hasNominations}
          hasAward={hasAward}
        />

        <Tab value={activeTab} index={GrantDetailsHeaderKey.OVERVIEW}>
          <ProgramOverview description={program.description} />
        </Tab>
        {hasNominations && (
          <Tab value={activeTab} index={GrantDetailsHeaderKey.NOMINATIONS}>
            <NominationsDetails nominations={candidate?.nominations ?? []} programName={program.name} />
          </Tab>
        )}
        <Tab value={activeTab} index={GrantDetailsHeaderKey.ORGANISATION}>
          <OrganisationDetails organisation={candidate?.organisation} />
        </Tab>
        <Tab value={activeTab} index={GrantDetailsHeaderKey.DOCUMENTS}>
          <CandidateDocuments documents={documents} />
        </Tab>
        {hasAward && (
          <Tab value={activeTab} index={GrantDetailsHeaderKey.DONATION}>
            <DonationDetails paymentDetails={candidate?.paymentDetails} award={candidate?.award} />
          </Tab>
        )}
      </Flex>

      <CandidateTasks
        tasksOpen={isTasksViewOpen}
        programId={program.id}
        tasks={tasks}
        candidateData={(candidate ?? {}) as CandidateData}
        refetchTasks={refetchTasks}
        refetchDocuments={refetchDocuments}
        onOpenDocuments={handleOpenDocuments}
      />
    </Flex>
  )
}
